import type { FormConfig } from "@/types/form-types"
import { create } from "zustand"
import { persist, createJSONStorage } from "zustand/middleware"

type FormStore = {
	applicationId: string | null
	setApplicationId: (applicationId: string) => void
	config: FormConfig | null
	setConfig: (config: FormConfig) => void
	verifiedFields: Map<string, boolean>
	setVerifiedFields: (
		fields:
			| { fieldId: string; isVerified: boolean }
			| { fieldId: string; isVerified: boolean }[]
			| Map<string, boolean>,
	) => void
	clearVerifiedFields: () => void
	submissionValues: Record<string, any>
	setSubmissionValues: (values: Record<string, any>) => void
	applicationSubmission: Record<string, any>
	setApplicationSubmission: (
		applicationSubmission: Record<string, any>,
	) => void
	currentStageIndex: number
	setCurrentStageIndex: (index: number) => void
	isPreview: boolean
	setIsPreview: (isPreview: boolean) => void
	shouldDisable: boolean
	setShouldDisable: (shouldDisable: boolean) => void
	isRenewal: boolean
	setIsRenewal: (isRenewal: boolean) => void
	leadDetails: {
		email_field_id: string
		mobile_field_id: string
	}
	setLeadDetails: (
		leadDetails: Partial<{
			email_field_id: string
			mobile_field_id: string
		}>,
	) => void
	reset: () => void
}

export const useFormStore = create<FormStore>()(
	persist(
		set => ({
			applicationId: null,
			setApplicationId: applicationId => set(() => ({ applicationId })),
			config: null,
			setConfig: config => set(() => ({ config })),
			verifiedFields: new Map(),
			setVerifiedFields: fields =>
				set(state => {
					const newMap = new Map(state.verifiedFields)

					if (fields instanceof Map) {
						fields.forEach((isVerified, fieldId) => {
							newMap.set(fieldId, isVerified)
						})
					} else {
						const updates = Array.isArray(fields)
							? fields
							: [fields]
						updates.forEach(f => {
							newMap.set(f.fieldId, f.isVerified)
						})
					}

					return { verifiedFields: newMap }
				}),
			clearVerifiedFields: () =>
				set(() => ({ verifiedFields: new Map() })),
			submissionValues: {},
			setSubmissionValues: values =>
				set(() => ({ submissionValues: values })),
			applicationSubmission: {},
			setApplicationSubmission: applicationSubmission =>
				set(() => ({ applicationSubmission })),
			currentStageIndex: 0,
			setCurrentStageIndex: index => set({ currentStageIndex: index }),
			isPreview: true,
			setIsPreview: isPreview => set({ isPreview }),
			shouldDisable: false,
			setShouldDisable: shouldDisable => set({ shouldDisable }),
			leadDetails: {
				email_field_id: "",
				mobile_field_id: "",
			},
			isRenewal: false,
			setIsRenewal: isRenewal => set({ isRenewal }),
			setLeadDetails: leadDetails =>
				set(state => {
					return {
						leadDetails: {
							...state.leadDetails,
							...leadDetails,
						},
					}
				}),
			reset: () =>
				set({
					applicationId: null,
					config: null,
					verifiedFields: new Map(),
					submissionValues: {},
					applicationSubmission: {},
					currentStageIndex: 0,
					isPreview: true,
					shouldDisable: false,
				}),
		}),
		{
			name: "form-store", // storage key
			partialize: state => ({
				currentStageIndex: state.currentStageIndex,
				// submissionValues: state.submissionValues,
				config: state.config,
				shouldDisable: state.shouldDisable,
				applicationId: state.applicationId,
			}), // only persist this field
			storage: createJSONStorage(() => sessionStorage),
		},
	),
)
